import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { updateLoginUserName } from "../Redux/BussinessPOCSlice";
import { fetchTracks } from "../Redux/TrackSlice";
import { fetchAdminUser } from "../Redux/AdminUserSlice";

import {
  Button, Box, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow,
  Typography, TextField, IconButton, Paper, Switch
} from "@mui/material";

import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

import EditConfirmationPopup from "./EditConfirmationPopup";
import EditTrackPopup from "./EditTrackPopup";
import AddMorePopup from "./AddMorePopup";
import ManageTh from "./ManageTh";

function ManageTracks() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const dispatch = useDispatch();

  const [searchVal, setSearchVal] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [openAddMore, setOpenAddMore] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState(null);
  const [previousSwitchState, setPreviousSwitchState] = useState(null);

  const [openEditTrack, setOpenEditTrack] = useState(false);
  const [editTrackDetails, setEditTrackDetails] = useState(null);
  const [trackDetails, setTrackDetails] = useState(null);

  const adminUsers = useSelector((state) => state?.adminUser?.adminUsers?.payload);
  const tracks = useSelector((state) => state?.tracks?.tracks);

  const [tracksData, setTracksData] = useState([]);
  const [trackStatusMap, setTrackStatusMap] = useState({});

  useEffect(() => {
    if (activeAccount) {
      dispatch(updateLoginUserName(activeAccount.idTokenClaims.enterpriseID));
    } else {
      dispatch(updateLoginUserName(null));
    }
  }, [activeAccount]);

  useEffect(() => {
    setLoadingSpinner(true);
    Promise.all([dispatch(fetchAdminUser()), dispatch(fetchTracks())])
      .finally(() => setLoadingSpinner(false));
  }, []);

  useEffect(() => {
    let trackData = [];
    let statusMap = {};

    tracks?.forEach((track, index) => {
      let th = [];
      adminUsers?.forEach((user) => {
        user?.assigned_tracks?.forEach((t, idx) => {
          if (t?.id === track.ID && t?.role?.includes('th')) {
            th.push({
              id: idx,
              thId: user?.id,
              enterpriseId: user?.EnterpriseID,
              isActive: t?.isActive,
            });
          }
        });
      });

      trackData.push({
        srno: index + 1,
        id: track.ID,
        track: track.Track,
        description: track.Details,
        createdBy: track.Created_by,
        isActive: track.IsActive,
        th,
      });

      statusMap[track.ID] = track.IsActive === 1;
    });

    setTracksData(trackData);
    setTrackStatusMap(statusMap);
  }, [tracks, adminUsers]);

  const theme = createTheme({
    typography: { fontFamily: "Graphik-Regular" },
  });

  const CssTextField = styled(TextField)({
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
    },
  });

  const searchFunction = (e) => {
    setSearchVal(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEditOpen = (id, track, th) => {
    setOpenEdit(true);
    setTrackDetails({ id, track, th });
  };

  const handleEditTrackOpen = (id, track, description) => {
    setOpenEditTrack(true);
    setEditTrackDetails({ id, trackName: track, trackDesc: description });
  };

  const handleAddOpen = () => {
    setOpenAddMore(true);
  };

  const handleStateChange = (track, id, newState) => {
    setPreviousSwitchState(trackStatusMap[id]);
    setTrackStatusMap((prev) => ({ ...prev, [id]: newState }));
    setConfirmationDetails({ track, id, state: newState });
    setOpenConfirmation(true);
  };

  const handleCancelConfirmation = () => {
    if (confirmationDetails?.id !== undefined && previousSwitchState !== null) {
      setTrackStatusMap((prev) => ({
        ...prev,
        [confirmationDetails.id]: previousSwitchState,
      }));
    }
  };

  const columns = [
    { id: "srno", label: "SL.#", align: "center" },
    { id: "track", label: "Track Name", align: "center" },
    { id: "description", label: "Track Description", align: "center", width: "27rem" },
    { id: "createdBy", label: "Created By", align: "center" },
    { id: "manageTh", label: "Manage TH", align: "center" },
    { id: "state", label: "State", align: "center" },
    { id: "editTrack", label: "Edit Track", align: "center" },
  ];

  const filteredData = tracksData?.filter((val) =>
    val?.track?.toLowerCase().includes(searchVal?.toLowerCase())
  );

  return (
    <ThemeProvider theme={theme}>
      <div className="banner training-banner-box">
        <img
          src={process.env.PUBLIC_URL + "/images/Home/training-banner.png"}
          alt="banner"
          className="home-banner"
        />
        <h1 className="banner-content">Manage Tracks</h1>
      </div>

      <Paper elevation={0} sx={{ width: "100%", px: "4rem", py: "2rem" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center">
            <CssTextField
              placeholder="Search by track name"
              value={searchVal}
              onChange={searchFunction}
              size="small"
              margin="dense"
              sx={{ m: 1, width: "30ch" }}
              InputProps={{
                endAdornment: (
                  <IconButton aria-label="search">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          <Button onClick={handleAddOpen}>Add New Track</Button>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 440, overflowY: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      top: 0,
                      width: column.width,
                      color: "#fff",
                      backgroundColor: "#550A69",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            {loadingSpinner ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress color="secondary" />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {filteredData?.length > 0 ? (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item) => (
                      <TableRow key={item.id}>
                        <TableCell align="center">{item.srno}</TableCell>
                        <TableCell align="center">{item.track}</TableCell>
                        <TableCell align="center">{item.description}</TableCell>
                        <TableCell align="center">{item.createdBy}</TableCell>
                        <TableCell
                          align="center"
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleEditOpen(item.id, item.track, item.th)}
                        >
                          <ManageAccountsIcon sx={{ color: "#c303f8ff", mt: "5px" }} />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={trackStatusMap?.[item.id] || false}
                            onChange={(e) => handleStateChange(item.track, item.id, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleEditTrackOpen(item.id, item.track, item.description)}
                        >
                          <img
                            src="/images/Home/edit-icon.png"
                            alt="edit"
                            style={{ width: "20px", height: "20px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No records found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredData?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Popups */}
      <AddMorePopup openAddMore={openAddMore} setOpenAddMore={setOpenAddMore} />
      <ManageTh openEdit={openEdit} setOpenEdit={setOpenEdit} trackDetails={trackDetails} />
      <EditTrackPopup openEditTrack={openEditTrack} setOpenEditTrack={setOpenEditTrack} editTrackDetails={editTrackDetails} />
      <EditConfirmationPopup
        openConfirmation={openConfirmation}
        setOpenConfirmation={setOpenConfirmation}
        details={confirmationDetails}
        onCancel={handleCancelConfirmation}
      />
    </ThemeProvider>
  );
}

export default ManageTracks;

